import React, { useRef, useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai';

import { ProPeriodTabType } from '@/constants/subscription';
import { proPeriodTabAtom } from '@/store';
import { getLocaleStore } from '@/i18n/localeStore';

import styles from './index.module.css';
import { track } from '@/utils/common';

export interface ProPeriodTabsProps {
  onChange?: (value: ProPeriodTabType) => void;
}

const { $t } = getLocaleStore();

const TABS: { value: ProPeriodTabType; label: string; badge?: string }[] = [
  {
    value: ProPeriodTabType.MONTHLY,
    label: $t((v) => v.Tab_RenewalMonth),
  },
  {
    value: ProPeriodTabType.ANNUAL,
    label: $t((v) => v.Tab_RenewalYear),
    badge: '立省30%',
  },
];

const INDICATOR_WIDTH = 44;

const ProPeriodTabs: React.FC<ProPeriodTabsProps> = ({ onChange }) => {
  const [active, setActive] = useAtom(proPeriodTabAtom);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({
    opacity: 0,
  });
  /** 仅用户点击切换二级 Tab 后才开启动效；首次进入/默认定位无动画 */
  const [enableMotion, setEnableMotion] = useState(false);

  const updateIndicator = useCallback(() => {
    const activeIndex = TABS.findIndex((tab) => tab.value === active);
    const activeTab = tabRefs.current[activeIndex];
    const wrapper = wrapperRef.current;
    if (!activeTab || !wrapper) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const centerX = tabRect.left - wrapperRect.left + tabRect.width / 2;

    setIndicatorStyle({
      width: INDICATOR_WIDTH,
      transform: `translateX(${centerX - INDICATOR_WIDTH / 2}px)`,
      opacity: 1,
    });
  }, [active]);

  useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  const handleClick = (value: ProPeriodTabType) => {
    if (value === active) return;
    setEnableMotion(true);
    setActive(value);
    onChange?.(value);

    track({
      event_id: 'vip_page_click',
      params: {
        period_type: value === ProPeriodTabType.MONTHLY ? 'monthly_recurring' : 'annual_recurring',
      },
    });
  };

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.motionEnabled]: enableMotion,
      })}
      ref={wrapperRef}
    >
      <div
        className={classNames(styles.indicator, {
          [styles.indicatorNoMotion]: !enableMotion,
        })}
        style={indicatorStyle}
      />
      {TABS.map((tab, index) => (
        <div
          key={tab.value}
          ref={(el) => {
            tabRefs.current[index] = el;
          }}
          className={classNames(styles.item, {
            [styles.itemActive]: tab.value === active,
          })}
          onClick={() => handleClick(tab.value)}
        >
          <span className={styles.label}>{tab.label}</span>
          {tab.badge ? (
            <span className={styles.badge}>
              <span className={styles.badgeText}>{tab.badge}</span>
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default React.memo(ProPeriodTabs);
